'use client'

import { useMemo } from 'react'

import { Message } from '~/components/component-library'
import { getAllChains, hasCapability } from '~/constants/chains'
import { useActiveOrg } from '~/hooks/org'
import { useBalanceTotalsByChain } from '~/hooks/useBalanceTotalsByChain'
import { useSmartAccounts } from '~/hooks/useSmartAccounts'
import { EvmAddress } from '~/utils/address'
import {
  getChainLabel,
  properlyFormatOneOrMoreChainNames,
} from '~/utils/chainLabels'
import { displayCurrencyShorthand } from '~/utils/display'

/**
 * Enabled networks that cannot appear in accounting. The org can hold and send
 * there, so a balance is expected, but `getAccountingChainIds` scopes every
 * accounting query to the `accounting` capability and drops them.
 */
const getExcludedChainIds = (orgChainIds: number[]): number[] => {
  const enabled = new Set(orgChainIds)
  return getAllChains()
    .filter(
      (chain) =>
        enabled.has(chain.chainId) &&
        !hasCapability(chain.chainId, 'accounting'),
    )
    .map((chain) => chain.chainId)
}

/**
 * Names the assets accounting leaves out, so a missing balance reads as a
 * network limit rather than a bug. Renders nothing when there is none to name.
 */
export const PartialNetworkAccountingBanner = ({
  accountIds,
  hasNetworkFilter,
}: {
  /** The Accounts filter. Absent or empty means every account. */
  accountIds?: string[]
  /** With a Networks filter the user has chosen the scope, so the banner stays silent. */
  hasNetworkFilter: boolean
}) => {
  const { org } = useActiveOrg()
  // The Accounts filter offers archived and automation accounts, and their
  // balances count towards the total either way.
  const { smartAccounts } = useSmartAccounts({
    showArchived: true,
    includeAutomations: true,
  })

  const excludedChainIds = useMemo(
    () =>
      hasNetworkFilter
        ? []
        : getExcludedChainIds(org?.smartAccountsChainIds ?? []),
    [hasNetworkFilter, org?.smartAccountsChainIds],
  )

  const addresses = useMemo(() => {
    const selected = new Set(accountIds ?? [])
    return smartAccounts
      .filter((account) => selected.size === 0 || selected.has(account.id))
      .map((account) => EvmAddress.format(account.address))
  }, [accountIds, smartAccounts])

  const heldByChainId = useBalanceTotalsByChain({
    addresses,
    chainIds: excludedChainIds,
  })

  const heldChainIds = excludedChainIds.filter(
    (chainId) => (heldByChainId[chainId] ?? 0) > 0,
  )
  if (heldChainIds.length === 0) return null

  const total = heldChainIds.reduce(
    (sum, chainId) => sum + heldByChainId[chainId],
    0,
  )

  return (
    <Message variant="info">
      <span>
        You have ~{displayCurrencyShorthand(total)} on{' '}
        {properlyFormatOneOrMoreChainNames(
          heldChainIds.map((chainId) => getChainLabel(chainId)),
        )}
        . Splits doesn&apos;t support accounting history on{' '}
        {heldChainIds.length > 1 ? 'these networks' : 'this network'}, so those
        assets aren&apos;t included here.
      </span>
    </Message>
  )
}
